import React, { useState, useEffect, useRef } from "react";
import ProgressBarWithCircles from "../Layout/ProgressBar";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import "../../common/common_styles.css";
import _ from "lodash";
import {
  setOverviewShown,
  selectOverviewShown,
  setAssignmentId,
  setRubricId,
  setStudentWorkId,
  setSampleWorkId,
  selectAssignmentId,
  selectRubricId,
  selectSampleWorkId,
  selectStudentWorkId,
  setGradingCalibrationId,
  setGradingCalibrationData,
  resetTeacherRubricGrades,
  setSessionName,
  setSessionDescription,
  selectSessionName,
  selectSessionDescription,
  setAssignmentDataWithoutChange, 
  setRubricDataWithoutChange, 
  setSampleWorkDataWithoutChange
} from "../../slices/gradingForm";
import {
  selectAssignmentData,
  selectRubricData,
  selectStudentWorkData,
  selectSampleWorkData,
} from "../../slices/gradingForm";
import RubricCriteria from "./RubricCriteria";
import WritingAssignmentCriteria from "./WritingAssignmentCriteria";
import StudentWorkCriteria from "./StudentWorkCriteria";
import SampleWorkCriteria from "./SampleWorkCriteria";
import FeedbackReview from "./FeedbackReview";
import {
  useRecordTextMutation,
  useGradeCalibrationMutation,
} from "../../slices/authApi";
import { PageLoadingSpinner } from "../UI/spinner/spinner";
import { useAppUsageLogger } from "../../slices/authApi";
import Modal from "../../common/InfoModal";
import classes from "./RubricCriteria.module.css";
import InstructionalVideo from "../ToolSelection/instructionalVideo";
import ToolDescription from "../../common/toolDescription";
import CompImage from "../../images/compre.png";
import InputModal from "../UI/input_modal/input_modal";
import SavedSessions from "./SavedSessions";
import SaveSessionInput from "./SaveSessionInput";
import {
  useSaveGradingSessionMutation,
} from "../../slices/authApi";
import ActionButton from "../UI/ActionButton/ActionButton";
import { selectLanguage } from "../../slices/auth";
import util from "../../styles/utilities.module.css";

const GradingTool = () => {
  let { step } = useParams(); // Retrieve the current step from the URL
  const location = useLocation();
  const language = useSelector(selectLanguage);

  const overviewShown = useSelector(selectOverviewShown);
  const assignmentData = useSelector(selectAssignmentData);
  const rubricData = useSelector(selectRubricData);
  const studentWorkData = useSelector(selectStudentWorkData);
  const sampleWorkData = useSelector(selectSampleWorkData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gradeError, setGradeError] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);

  const assignmentId = useSelector(selectAssignmentId);
  const rubricId = useSelector(selectRubricId);
  const sampleWorkId = useSelector(selectSampleWorkId);
  const studentWorkId = useSelector(selectStudentWorkId);
  const [gradeCalibration] = useGradeCalibrationMutation();
  const sessionName = useSelector(selectSessionName);
  const sessionDescription = useSelector(selectSessionDescription);
  const [localName, setLocalName] = useState(sessionName);
  const [localDesc, setLocalDesc] = useState(sessionDescription);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveChanges, setSaveChanges] = useState(false);
  const recordDataRef = useRef(null);

  const [showsSessionLoadedModal, setShowsSessionLoadedModal] = useState(false);

  const gradingFormSteps = {
    assignment: {
      data: assignmentData,
      idSetter: setAssignmentId,
      idGetter: () => assignmentId,
      nextStep: "rubric",
      previousStep: "/toolSelection",
      progress: 16,
      mainComponent: WritingAssignmentCriteria,
      buttonText: "Next",
    },
    rubric: {
      data: rubricData,
      idSetter: setRubricId,
      idGetter: () => rubricId,
      nextStep: "sampleWork",
      previousStep: "/grading/assignment",
      progress: 32,
      mainComponent: RubricCriteria,
      buttonText: "Next",
    },
    sampleWork: {
      data: sampleWorkData,
      idSetter: setSampleWorkId,
      idGetter: () => sampleWorkId,
      progress: 48,
      nextStep: "studentWork",
      previousStep: "/grading/rubric",
      mainComponent: SampleWorkCriteria,
      buttonText: "Next",
    },
    studentWork: {
      data: studentWorkData,
      idSetter: setStudentWorkId,
      idGetter: () => studentWorkId,
      nextStep: "rubricGradesReview",
      previousStep: "/grading/sampleWork",
      progress: 64,
      mainComponent: StudentWorkCriteria,
      buttonText: "Next",
    },
    rubricGradesReview: {
      nextStep: "feedbackReview",
      previousStep: "/grading/studentWork",
      progress: 80,
      mainComponent: FeedbackReview,
      buttonText: "Next",
    },
    feedbackReview: {
      nextStep: "",
      previousStep: "/grading/rubricGradesReview",
      progress: 100,
      mainComponent: FeedbackReview,
      buttonText: "Go back home",
    },
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const logAppUse = useAppUsageLogger();

  async function navigateToPreviousPage() {
    setGradeError(false);
    const currentStep = gradingFormSteps[step];
    if (currentStep) {
      logAppUse(step, currentStep.previousStep);
      navigate(currentStep.previousStep);
    }
  }

  const [recordText] = useRecordTextMutation();
  const [errorMsg, setErrMsg] = useState("");

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveGradingSession] = useSaveGradingSessionMutation();

  useEffect(() => {
    if (showSaveModal) {
      setLocalName(sessionName);
      setLocalDesc(sessionDescription);
    }
  }, [showSaveModal, sessionName, sessionDescription]);

  const handleSaveSession = async () => {
    dispatch(setSessionName(localName));
    dispatch(setSessionDescription(localDesc));

    try {
      const sessionData = {
        name: localName,
        description: localDesc,
        assignment_id: assignmentId,
        rubric_id: rubricId,
        sample_work_id: sampleWorkId,
      };
      // if(location.pathname.includes("sampleWork")){
      //   delete sessionData.sample_work_id;
      // }
      await saveGradingSession(sessionData).unwrap();
      setShowSaveModal(false);

      const updatedSampleWorkData = {...sampleWorkData, changed: false};
      const updatedRubricData = _.cloneDeep(rubricData);
      updatedRubricData.changed = false;
      const updatedAssignmentData = {...assignmentData, changed: false};

      dispatch(setSampleWorkDataWithoutChange(updatedSampleWorkData));
      dispatch(setAssignmentDataWithoutChange(updatedAssignmentData));
      dispatch(setRubricDataWithoutChange(updatedRubricData));

      // Show a success message here maybe?
      setSaveSuccess(true);
    } catch (err) {
      console.error("Failed to save session:", err);
      // Show an error message here maybe?
    }
  };


  const isEmpty = (obj) => {
    const sampleWorkKeys = Object.keys(obj).filter((key) =>
      key.match(/^sampleWork\[\d+\]$/)
    );

    for (const key of sampleWorkKeys) {
      if (obj[key] !== "") {
        return false; // At least one sampleWork property is non-empty
      }
    }

    // Check the length of _files arrays directly
    const filesKeys = Object.keys(obj).filter((key) =>
      key.match(/^sampleWork\[\d+\]_files$/)
    );
    for (const key of filesKeys) {
      if (Array.isArray(obj[key]) && obj[key].length > 0) {
        return false; // The _files array has items
      }
    }

    return true; // All sampleWork properties are empty and their corresponding _files arrays are empty or not present
  };

  function checkSampleWorks(obj) {
    const emptyGradeProperties = [];

    for (const key in obj) {
      if (
        obj.hasOwnProperty(key) &&
        key.match(/^sampleWork\[\d+\]_grade$/) &&
        obj[key] === ""
      ) {
        emptyGradeProperties.push(key); // If property matches the pattern and has an empty value, push it to the array
      }
    }

    return emptyGradeProperties.length > 0
      ? emptyGradeProperties
      : !Object.keys(obj).some((key) => key.match(/^sampleWork\[\d+\]_grade$/));
  }

  useEffect(() => {
    setErrMsg("");
  }, [step]);

  const goBackHandler = () => {
    logAppUse("no_samples_pop_up", "sample_work");
    setError("");
  };

  async function handleSubmit(event) {
    event.preventDefault();
    setErrMsg(""); // Clear previous errors

    if (step === "feedbackReview") {
      // This form handles its own submissions
      return;
    }

    // --- Start of Added Validation ---
    if (step === "assignment") {
      // Check if subject or grade is empty or null
      if (!assignmentData?.subject || !assignmentData?.grade) {
        setErrMsg("Please select both Subject and Grade before continuing.");
        return; // Prevent moving to the next step
      }
    }
    // --- End of Added Validation ---

    if (step === "sampleWork") {
      
      // Clear out gradingCalibrationId and gradingCalibrationData, in case we have existing data
      dispatch(setGradingCalibrationId(null));
      dispatch(setGradingCalibrationData(null));
      
      const noSampleProvided = isEmpty(sampleWorkData);
      if (noSampleProvided) {
        logAppUse("sample_work", "no_samples_pop_up");
        setError("no_sample");
        return;
      } else {
        const sampleWorksWithNoGrade = checkSampleWorks(sampleWorkData);
        if (
          sampleWorksWithNoGrade.length > 0 ||
          sampleWorksWithNoGrade === true
        ) {
          setGradeError(true);
          return;
        } else {
          setGradeError(false);
          setShowPopUp(true);
          logAppUse("sample_work", "more_samples_pop_up");
          return;
        }
      }
    } else if (step === "studentWork") {
      // If coming from student work page, clear out any assigned grades from before
      dispatch(resetTeacherRubricGrades());
    }

    // Access the correct data based on the step
    const currentStep = gradingFormSteps[step];
    if (!currentStep) {
      setErrMsg("Invalid step");
      return;
    }
    
    const dataToRecord = {
      ...currentStep.data, // Clone and extend the data object
      language: language,
      //content_id: currentStep.id,  // Add content_id to the data object
    };

    try {
      setLoading(true);
      const recordData = await recordText({
        body: dataToRecord,
        record_location: step,
      }).unwrap();

      dispatch(currentStep.idSetter(recordData.text_id));

      logAppUse(step, currentStep.nextStep);
      const fileMetadata = recordData.file_metadata || []; // file_metadata from the backend

      // Navigate to the next step and pass file metadata as state
      navigate("/grading/" + currentStep.nextStep, {
        state: { fileMetadata },
      });
    } catch (err) {
      if (!err?.status) {
        // isLoading: true until timeout occurs
        setErrMsg("No Server Response");
      } else if (err.status === 401) {
        setErrMsg("Unauthorized");
      } else {
        let err_message = "";
        if (err.data.message) {
          err_message = err.data.message;
        } else {
          err_message = err.status + " Server Error ";
        }
        setErrMsg(err_message);
      }
    } finally {
      setLoading(false);
    }
  }

  const getGradeCalibration = async (newSampleWorkId) => {
    try {
      const calibrationData = await gradeCalibration({
        assignment_id: assignmentId,
        rubric_id: rubricId,
        sample_work_id: newSampleWorkId,
      });

      dispatch(
        setGradingCalibrationData(calibrationData?.data?.grading_calibration)
      );
      dispatch(setGradingCalibrationId(calibrationData?.data?.grading_id));
    } catch (err) {
      if (!err?.status) {
        setErrMsg("No Server Response");
      } else if (err.status === 401) {
        setErrMsg("Unauthorized");
      } else {
        let err_message = "";
        if (err.data.message) {
          err_message = err.data.message;
        } else {
          err_message = err.status + " Server Error ";
        }
        setErrMsg(err_message);
      }
    }
  };

  const continuePastSamplesHandler = async () => {
    // Access the correct data based on the step
    const currentStep = gradingFormSteps[step];
    if (!currentStep) {
      setErrMsg("Invalid step");
      return;
    }
    
    const dataToRecord = {
      ...currentStep.data, // Clone and extend the data object
      content_id: currentStep.idGetter(), // Add content_id to the data object
    };
    // Clear localStorage:preserveData which we use to determine if we are just coming back from Google authentication
    // so that we will re-get feedback if we went to Google auth and then back here
    localStorage.removeItem("preserveData");

    try {
      setLoading(true);
      const recordData = await recordText({
        body: dataToRecord,
        record_location: step,
      }).unwrap();
      logAppUse("more_samples_pop_up", "student_work");

      const updatedSampleWorkId = recordData.text_id;
      dispatch(currentStep.idSetter(updatedSampleWorkId));

      // Kick off getting grade calibration for use in grading student work
      getGradeCalibration(updatedSampleWorkId);
      const fileMetadata = recordData.file_metadata || []; // file_metadata from the backend

      navigate("/grading/" + currentStep.nextStep, {
        state: { fileMetadata },
      });
    } catch (err) {
      if (!err?.status) {
        // isLoading: true until timeout occurs
        setErrMsg("No Server Response");
      } else if (err.status === 401) {
        setErrMsg("Unauthorized");
      } else {
        let err_message = "";
        if (err.data.message) {
          err_message = err.data.message;
        } else {
          err_message = err.status + " Server Error ";
        }
        setErrMsg(err_message);
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelHandler = () => {
    setShowPopUp(false);
    logAppUse("more_samples_pop_up", "sample_work");
  };

  const checkChanged = () => {
    if (
      assignmentData.changed ||
      sampleWorkData.changed ||
      rubricData.changed
    ) {
      return true;
    } else {
      return false;
    }
  };

  const yesHandler = async () => {
    setShowSaveModal(true);
    setSaveChanges(false);
    const currentStep = gradingFormSteps[step];
    if (!currentStep) {
      setErrMsg("Invalid step");
      return;
    }
    
    const dataToRecord = {
      ...currentStep.data, // Clone and extend the data object
    };
    try {
      setLoading(true);
      const recordData = await recordText({
        body: dataToRecord,
        record_location: step,
      }).unwrap();

      dispatch(currentStep.idSetter(recordData.text_id));
      recordDataRef.current = recordData;

    } catch (err) {
      if (!err?.status) {
        // isLoading: true until timeout occurs
        setErrMsg("No Server Response");
      } else if (err.status === 401) {
        setErrMsg("Unauthorized");
      } else {
        let err_message = "";
        if (err.data.message) {
          err_message = err.data.message;
        } else {
          err_message = err.status + " Server Error ";
        }
        setErrMsg(err_message);
      }
    } finally {
      setLoading(false);
    }


  }

  // Add null safety check for gradingFormSteps[step]
  const currentStep = gradingFormSteps[step];
  const MainComponent = currentStep?.mainComponent;

  useEffect(() => {
    if (sessionStorage.getItem("showModal") === "true") {
      setShowsSessionLoadedModal(true);
    } else {
      setShowsSessionLoadedModal(false);
    }
  }, []);


  return (
    <>
      {saveChanges ? (
        <div>
          <Modal>
            <div className={classes.infoModalContainer}>
              <p>
              Save writing assignment to allow loading and grading more students later?
              </p>
              <div className={classes.infoModalButtonsContainer}>
                <button 
                  onClick={()=>{ setSaveChanges(false); }} 
                  className={`${util.btn} ${util['btn--ghost']}`}
                >
                  No
                </button>
                <button
                  onClick={yesHandler}
                  className={`${util.btn} ${util['btn--primary']}`}
                >
                  Yes
                </button>
              </div>
            </div>
          </Modal>
        </div>
      ) : (
        <></>
      )}

      {saveSuccess ? (
        <Modal>
          <div className={classes.infoModalContainer}>
            <p>Session saved successfully</p>
            <div style={{ textAlign: "center" }}>
              <button
                className={`${util.btn}`}
                onClick={() => {setSaveSuccess(false); 
                  if(location.pathname.includes("sampleWork")){
                  
                    const fileMetadata = recordDataRef.current.file_metadata || []; // file_metadata from the backend
                    // Navigate to the next step and pass file metadata as state
                    navigate("/grading/" + gradingFormSteps[step].nextStep, {
                      state: { fileMetadata },
                    });
                }}}
                style={{ margin: "20px 0px 0px 0px" }}
              >
                Ok
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {error === "no_sample" ? (
        <div>
          <Modal>
            <div className={classes.infoModalContainer}>
              <p>
                You have not submitted any graded samples. This may impact the
                quality of the grading. Graded samples help the AI learn your
                grading standards. We highly recommend adding one sample for
                each level of your grading scale.
              </p>
              <div className={classes.infoModalButtonsContainer}>
                <button onClick={goBackHandler} className={`${util.btn} ${util['btn--ghost']}`}>
                  Go back
                </button>
                <button
                  onClick={() => {
                        setError("");
                        setGradeError(false);
                    if (checkChanged()) {
                      setSaveChanges(true);
                    }
                    continuePastSamplesHandler();
                  }}
                  className={`${util.btn} ${util['btn--primary']}`}
                >
                  Continue
                </button>
              </div>
            </div>
          </Modal>
        </div>
      ) : (
        <></>
      )}

      {showPopUp ? (
        <div>
          <Modal>
            <div className={classes.infoModalContainer}>
              <p>
                Did you put in a sample for each level of your grading scale?
                Graded samples help the AI learn your grading standards. Click
                "Continue" to go to the next step or "Cancel" to add more
                samples.
              </p>
              <div className={classes.infoModalButtonsContainer}>
                <button
                  onClick={cancelHandler}
                  className={`${util.btn} ${util['btn--ghost']}`}
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                      setGradeError(false);
                      setShowPopUp(false);
                    if (checkChanged()) {
                      setSaveChanges(true);
                    }
                    continuePastSamplesHandler();
                  }}
                  className={`${util.btn} ${util['btn--primary']}`}
                >
                  Continue
                </button>
              </div>
            </div>
          </Modal>
        </div>
      ) : (
        <></>
      )}

      {!overviewShown ? (
        <Modal>
          <div className={classes.infoModalContainer}>
            <p>
              This tool will help you grade a writing assignment. Please have
              the following information ready:
            </p>
            <br></br>
            <ol>
              <li>The writing assignment or writing prompt</li>
              <li>Your rubric (optional but recommnended)</li>
              <li>A graded sample for each level of your grading scale</li>
              <li>The student's work</li>
            </ol>
            <div style={{ textAlign: "center" }}>
              <button
                className={`${util.btn}`}
                onClick={() => dispatch(setOverviewShown(true))}
                style={{ margin: "20px 0px 0px 0px" }}
                data-testid="overview-modal-continue"
              >
                Continue
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      {showsSessionLoadedModal ? (
        <Modal>
          <div className={classes.infoModalContainer}>
            <p>
              You can modify the loaded grading criteria or simply click
              continue to move on to grading student work.
            </p>
            <br></br>

            <div style={{ textAlign: "center" }}>
              <button
                className={`${util.btn}`}
                onClick={() => {
                  setShowsSessionLoadedModal(false);
                  sessionStorage.removeItem("showModal");
                }}
                style={{ margin: "20px 0px 0px 0px" }}
              >
                ok
              </button>
            </div>
          </div>
        </Modal>
      ) : null}

      <div className="page-margin">
        {loading ? <PageLoadingSpinner /> : <></>}
        <section className="app-main" data-testid="grading-workspace">
          <div role="region" aria-label="grading">
            <ProgressBarWithCircles
              progress={currentStep?.progress || 0}
            />
            {step === "assignment" ? (
              <>
                <div className="videoAndTool">
                  <ToolDescription
                    image={CompImage}
                    heading={"Get assistance with grading writing assignments"}
                    description={`This tool relieves you of the tedious task of grading student papers while keeping you firmly in control. 
                            Simply upload your assignment, rubric (if you have one), a few graded samples, and the student work you 
                            want to grade. Our AI will suggest grades and detailed feedback aligned with your criteria. And the best 
                            part? You're always in control—you can adjust grades or personalize feedback however you like. Once you're 
                            happy, you can export everything to Google Docs.`}
                  />

                  <InstructionalVideo
                    formType={"grading"}
                    logAppUse={logAppUse}
                  />
                </div>

                <SavedSessions />

                <br />
              </>
            ) : null}

            {(step === "studentWork" ||
              step === "rubricGradesReview" ||
              step === "feedbackReview") && checkChanged()&& (
              // <button onClick={() => setShowSaveModal(true)}>Save Session</button>
             
              <div className="saveButtonContainer">
                <p>Writing assignment unsaved</p>
              <ActionButton
                text={"Save Session"}
                customClass="saveSessionButton"
                handleOnClick={() => setShowSaveModal(true)}
                tooltiptext={
                  "Saving the assignment allows you to load the prompt, rubric, and sample work you've entered later to do additional grading"
                }
              />
              </div>
            )}

            <InputModal
              modalProps={{
                isOpen: showSaveModal,
                description: "Save your current grading session",
                leftBtnHandler: () => setShowSaveModal(false),
                rightBtnHandler: handleSaveSession,
                buttons: {
                  leftBtn: "Cancel",
                  rightBtn: "Save",
                },
                testId: "save-session-modal"
              }}
              InputComponent={SaveSessionInput}
              inputProps={{
                name: localName,
                desc: localDesc,
                onNameChange: (e) => setLocalName(e.target.value),
                onDescChange: (e) => setLocalDesc(e.target.value),
              }}
            />

            {MainComponent ? (
              <form onSubmit={handleSubmit}>
                <MainComponent
                  step={step}
                  previousStep={currentStep?.previousStep}
                />

                {errorMsg ? <div className={`${util.alert} ${util['alert--danger']}`}>{errorMsg}</div> : null}

                {gradeError ? (
                  <div className={`${util.alert} ${util['alert--danger']}`}>
                    {"Enter a grade for each sample"}
                  </div>
                ) : null}

                {!(MainComponent === FeedbackReview) ? (
                  <div className="navigationButtonsContainer">
                    <div className="form-actions form-actions--flow">
                      <button
                        type="button"
                        className={`${util.btn} ${util['btn--ghost']} navigationButton`}
                        onClick={navigateToPreviousPage}
                      >
                        Back
                      </button>
                      <button data-testid="form-navigation-continue" className={`${util.btn} ${util['btn--secondary']} navigationButton`} type="submit">
                        Continue
                      </button>
                    </div>
                  </div>
                ) : null}
              </form>
            ) : (
              <div className={`${util.alert} ${util['alert--danger']}`}>
                <h4>Invalid step: {step}</h4>
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default GradingTool;
